import { hexToNormalizedRgba } from '@/utils/color-kit';
import { createSVGElement } from '@/utils/svg-kit';
import type { BoundingBox } from '@/version-types';
import { GLOW_BLUR_SIGMA_RATIO, GLOW_DILATE_RATIO, GLOW_MIN_BLUR_SIGMA, SHADOW_BLUR_SIGMA_RATIO } from '../constants';
import type { TextRenderGlow, TextRenderInput, TextRenderShadow } from '../types';

/**
 * 当前一轮正文 filter 使用的阴影、外发光与私有资源 ID。
 */
export type TextFilter = Readonly<{
    shadow?: TextRenderShadow;
    glow?: TextRenderGlow;
    id?: string;
}>;

/**
 * 解析普通文字可见 effects 及其累计描边宽度。
 *
 * @param input - 当前 Text Render 输入。
 * @param namespace - 当前宿主图层 ID，用作 SVG 资源命名空间。
 * @returns 同步 SVG pipeline 可消费的 effects 状态。
 */
export const resolveFilter = (input: TextRenderInput, namespace: string): TextFilter => {
    const shadow = input.shadows?.find((item) => !item.hidden);
    const glow = input.glows?.find((item) => !item.hidden && item.width > 0);
    return { shadow, glow, ...(shadow || glow ? { id: `${namespace}-effect` } : {}) };
};

/**
 * 创建 effects filter defs 并返回正文绘制目标。
 *
 * @param svg - 当前 SVG 根节点。
 * @param filterState - 当前一轮准备好的 filter 状态。
 * @param renderBox - 已扩展的可见区域。
 * @returns 正文、描边与填充应追加到的节点。
 */
export const createFilterTarget = (svg: SVGSVGElement, filterState: TextFilter, renderBox: BoundingBox): SVGElement => {
    if (!filterState.id) return svg;
    const defs = createSVGElement('defs');
    const filter = createSVGElement('filter', {
        id: filterState.id,
        filterUnits: 'userSpaceOnUse',
        'color-interpolation-filters': 'sRGB',
        x: renderBox.left,
        y: renderBox.top,
        width: renderBox.width,
        height: renderBox.height,
    });

    const transparent = createSVGElement('feFlood', {
        'flood-opacity': 0,
        result: 'BackgroundImageFix',
    });
    filter.appendChild(transparent);
    const sourceAlpha = createSVGElement('feColorMatrix', {
        in: 'SourceAlpha',
        type: 'matrix',
        values: '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0',
        result: 'hardAlpha',
    });
    filter.appendChild(sourceAlpha);
    let lastResult = 'BackgroundImageFix';

    if (filterState.glow) {
        const { r, g, b, a } = hexToNormalizedRgba(filterState.glow.color);
        const morphology = createSVGElement('feMorphology', {
            in: 'SourceAlpha',
            operator: 'dilate',
            radius: filterState.glow.width * GLOW_DILATE_RATIO,
            result: 'glowDilated',
        });
        filter.appendChild(morphology);
        const blur = createSVGElement('feGaussianBlur', {
            in: 'glowDilated',
            stdDeviation: Math.max(filterState.glow.width * GLOW_BLUR_SIGMA_RATIO, GLOW_MIN_BLUR_SIGMA),
            result: 'glowBlurred',
        });
        filter.appendChild(blur);
        const composite = createSVGElement('feComposite', {
            in: 'glowBlurred',
            in2: 'SourceAlpha',
            operator: 'out',
            result: 'glowCropped',
        });
        filter.appendChild(composite);
        const color = createSVGElement('feColorMatrix', {
            type: 'matrix',
            values: `0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} 0 0 0 ${a} 0`,
            result: 'glowColored',
        });
        filter.appendChild(color);
        const blend = createSVGElement('feBlend', {
            mode: 'normal',
            in: 'glowColored',
            in2: lastResult,
            result: 'effect1_outerGlow',
        });
        filter.appendChild(blend);
        lastResult = 'effect1_outerGlow';
    }
    if (filterState.shadow) {
        const { r, g, b, a } = hexToNormalizedRgba(filterState.shadow.color);
        const offset = createSVGElement('feOffset', {
            in: 'SourceAlpha',
            dx: filterState.shadow.x,
            dy: filterState.shadow.y,
            result: 'shadowOffset',
        });
        filter.appendChild(offset);
        const blur = createSVGElement('feGaussianBlur', {
            in: 'shadowOffset',
            stdDeviation: Math.max(0, filterState.shadow.blur) * SHADOW_BLUR_SIGMA_RATIO,
            result: 'shadowBlurred',
        });
        filter.appendChild(blur);
        const composite = createSVGElement('feComposite', {
            in: 'shadowBlurred',
            in2: 'SourceAlpha',
            operator: 'out',
            result: 'shadowCropped',
        });
        filter.appendChild(composite);
        const color = createSVGElement('feColorMatrix', {
            type: 'matrix',
            values: `0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} 0 0 0 ${a} 0`,
            result: 'shadowColored',
        });
        filter.appendChild(color);
        const blend = createSVGElement('feBlend', {
            mode: 'normal',
            in: 'shadowColored',
            in2: lastResult,
            result: 'effect2_dropShadow',
        });
        filter.appendChild(blend);
        lastResult = 'effect2_dropShadow';
    }
    const finalBlend = createSVGElement('feBlend', {
        mode: 'normal',
        in: 'SourceGraphic',
        in2: lastResult,
        result: 'shape',
    });
    filter.appendChild(finalBlend);
    defs.appendChild(filter);
    svg.appendChild(defs);

    const group = createSVGElement('g', { filter: `url(#${filterState.id})` });
    svg.appendChild(group);
    return group;
};
